import { useState, useEffect } from "react";
import { Link, Navigate } from "react-router-dom";
import Navbar from "../../../components/feature/Navbar";
import Footer from "../../../components/feature/Footer";
import LearningProgress from "./components/LearningProgress";
import FollowedProviders from "./components/FollowedProviders";
import PurchaseHistory from "./components/PurchaseHistory";
import MyMessages from "./components/MyMessages";
import ProfileSettings from "./components/ProfileSettings";
import ReviewableHistory from "./components/ReviewableHistory";
import RecommendedCourses from "./components/RecommendedCourses";
import BookmarkList from "./components/BookmarkList";
import { useApp } from "../../../contexts/AppContext";
import { supabase } from "../../../lib/supabase";

type Tab = "learning" | "bookmarks" | "following" | "history" | "messages" | "settings";

const tabs: { id: Tab; label: string; icon: string }[] = [
  { id: "learning", label: "学習中", icon: "ri-play-circle-line" },
  { id: "bookmarks", label: "コレクション", icon: "ri-bookmark-line" },
  { id: "following", label: "フォロー中", icon: "ri-user-follow-line" },
  { id: "history", label: "購入履歴", icon: "ri-shopping-bag-line" },
  { id: "messages", label: "メッセージ", icon: "ri-message-3-line" },
  { id: "settings", label: "設定", icon: "ri-settings-3-line" },
];

export default function ConsumerMypagePage() {
  const [activeTab, setActiveTab] = useState<Tab>("learning");
  const { currentUser, isLoggedIn, authLoading } = useApp();
  const [totalUnread, setTotalUnread] = useState(0);

  useEffect(() => {
    if (!currentUser) return;
    supabase.from("conversations").select("consumer_unread").eq("consumer_id", currentUser.id)
      .then(({ data }) => {
        if (data) setTotalUnread(data.reduce((s: number, c: { consumer_unread: number }) => s + (c.consumer_unread ?? 0), 0));
      });
  }, [currentUser]);

  useEffect(() => {
    if (!currentUser) return;
    const channel = supabase.channel("consumer-unread")
      .on("postgres_changes", { event: "UPDATE", schema: "public", table: "conversations", filter: `consumer_id=eq.${currentUser.id}` }, () => {
        supabase.from("conversations").select("consumer_unread").eq("consumer_id", currentUser.id)
          .then(({ data }) => {
            if (data) setTotalUnread(data.reduce((s: number, c: { consumer_unread: number }) => s + (c.consumer_unread ?? 0), 0));
          });
      }).subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [currentUser]);

  if (authLoading) return <div className="min-h-screen bg-ivory flex items-center justify-center"><Navbar /><i className="ri-loader-4-line animate-spin text-3xl text-teal" /></div>;
  if (!isLoggedIn) return <Navigate to="/login" replace />;

  return (
    <div className="min-h-screen bg-ivory">
      <Navbar />
      <main className="pt-20">
        <section className="bg-deep-green py-10 px-4">
          <div className="max-w-screen-xl mx-auto flex flex-col md:flex-row items-start md:items-center gap-6">
            <div className="w-16 h-16 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0">
              <i className="ri-user-3-line text-white text-3xl" />
            </div>
            <div className="flex-1">
              <p className="font-display italic text-gold text-xs tracking-widest mb-1">My Page</p>
              <h1 className="font-serif text-white text-2xl font-light">{currentUser?.name ?? "ゲスト"} さん</h1>
              <p className="text-white/50 text-sm font-sans mt-1">{currentUser?.email ?? ""}</p>
            </div>
            <div className="flex gap-3 flex-shrink-0">
              <Link to="/mypage/provider" className="text-xs border border-white/30 text-white/80 px-4 py-2 rounded-full font-sans cursor-pointer hover:bg-white/10 transition-colors whitespace-nowrap">
                <i className="ri-store-2-line mr-1" />事業者モードに切替
              </Link>
            </div>
          </div>
        </section>

        <section className="bg-white border-b border-stone-100 sticky top-16 z-30 px-4">
          <div className="max-w-screen-xl mx-auto flex gap-1 overflow-x-auto">
            {tabs.map((t) => (
              <button key={t.id} onClick={() => setActiveTab(t.id)}
                className={`flex items-center gap-1.5 px-3 md:px-4 py-4 text-xs md:text-sm font-sans whitespace-nowrap cursor-pointer border-b-2 transition-all duration-200 relative ${activeTab === t.id ? "border-deep-green text-deep-green" : "border-transparent text-charcoal/50 hover:text-charcoal"}`}>
                <i className={t.icon} />
                <span className="hidden sm:inline">{t.label}</span>
                {t.id === "messages" && totalUnread > 0 && (
                  <span className="bg-gold text-white text-xs rounded-full w-4 h-4 flex items-center justify-center font-sans">{totalUnread}</span>
                )}
              </button>
            ))}
          </div>
        </section>

        <section className="px-4 py-8">
          <div className="max-w-screen-xl mx-auto">
            {activeTab === "learning" && (<><ReviewableHistory /><LearningProgress /><RecommendedCourses /></>)}
            {activeTab === "bookmarks" && <BookmarkList />}
            {activeTab === "following" && <FollowedProviders />}
            {activeTab === "history" && <PurchaseHistory />}
            {activeTab === "messages" && <MyMessages />}
            {activeTab === "settings" && <ProfileSettings />}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
